import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import * as d3 from 'd3';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import MenuIcon from '@mui/icons-material/Menu';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';
import { CONSTANTS } from 'src/components/Constants';
import { AppContext, UserContext, ThemeContext } from 'src/store/ContextStore';
import { usePedigreeInstance } from '../pedigree-tool/pedigree/instanceContext';
import useVAFeatureFlags from 'src/hooks/useVAFeatureFlags';
import {
  getUserSidebarItems,
  SIDEBAR_ITEMS,
  SIDEBAR_VALUE,
  STUDENT_SIDEBAR_ITEMS
} from './util';
import { isAnonymousActive } from '../anonymousPedigrees';
import Tags from './sections/Tags';
import PersonTags from './sections/PersonTags';
import ExternalFamilyIds from 'src/components/proband-info/ExternalFamilyIds';
import FamilyNote from 'src/components/proband-info/FamilyNote';
import LabelConfigurations from './sections/LabelConfigurations';
import LegendMenu from './sections/LegendMenu';
import PhenotypesLegend from './sections/PhenotypesLegend';
import GenotypesLegend from './sections/GenotypesLegend';
import LabelsMenu from './sections/LabelsMenu';
import PersonMenu from './sections/PersonMenu';
import ReproductiveSection from './sections/ReproductiveSection';
import Suggestions from './sections/Suggestions';
import PedigreeMatchMenu from './sections/PedigreeMatchMenu';
import FormSubmission from './sections/FormSubmission';
import AddRelativeMenu from '../pedigree-tool/canvas-tools/options/AddRelativeMenu';
import DiagnosesMenu from './sections/diagnoses-menu/DiagnosesMenu';
import GenotypesMenu from './sections/genotypes-menu/GenotypesMenu';
import PhenotypesMenu from './sections/phenotypes-menu/PhenotypesMenu';
import RiskFactorsMenu from './sections/RiskFactorsMenu';
import FamilyHistoryMenu from './sections/FamilyHistoryMenu';
import ExportMenu from './sections/export-menu/ExportMenu';
import EpicEmrButtonWrapper from 'src/components/integrations/epic/EpicEmrButton';
import ClinicalNotes from './sections/clinical-notes-section/ClinicalNotes';
import Pathways from './sections/Pathways';
import RecordStatusMenu from './sections/RecordStatusMenu';
import { APICONSTANTS } from 'src/components/Constants';
import usePersonLinkStatus from 'src/hooks/usePersonLinkStatus';
import axios from 'src/axios';
import { useDirtyState } from 'src/hooks/useDirtyState';
import SaveFooter from './SaveFooter';
import SidebarLockBanner from './SidebarLockBanner';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModificationHistoryModal from './ModificationHistoryModal';
import SidebarSection from './SidebarSection';

const StyledList = styled('div')(({ expand, full }) => ({
  width: full ? 'auto' : expand ? '95vw' : 400,
  transition: 'width 1s ease',
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
}));

const BtnContainer = styled('div')({
  position: 'absolute',
  right: 0,
  top: 0,
  zIndex: 10
});

const ListContainer = styled(List)({
  overflowX: 'hidden'
});

const HeaderSection = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  // Inset the family-number title from the sidebar edge (it used to sit behind
  // the removed expand arrow). 12px on the left matches the close button's
  // right inset (6px here + the button's own 6px padding).
  paddingLeft: 12,
  paddingRight: 6,
  // Breathing room above and below the whole header row (family number and
  // close button alike) so it isn't cramped against the sidebar top edge.
  paddingTop: 12,
  paddingBottom: 12
});

const CloseBtn = styled(IconButton)({
  marginLeft: 'auto',
  // Tighter hover halo, in line with the app's standard icon buttons (the
  // previous size="large" made the grey circle oversized next to the icon).
  padding: 6
});

const ScrollableContent = styled('div')({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden'
});

const modalTitleMap = {
  [CONSTANTS.auditLogType.clinicalRecord]: 'Clinical History',
  [CONSTANTS.auditLogType.riskFactors]: 'Risk History',
  [CONSTANTS.auditLogType.phenotypes]: 'Phenotypes History',
  [CONSTANTS.auditLogType.genotypes]: 'Genotypes History',
  [CONSTANTS.auditLogType.diagnoses]: 'Disease History',
  [CONSTANTS.auditLogType.personDetails]: 'Person History'
};

// The sidebar accordion row (styled Accordion + summary/details) lives in
// SidebarSection, shared with the legacy patient sidebar and gated by the
// PEDIGREE_SIDEBAR_REFRESH flag.

export default function PedigreeSideBar({
  data,
  sidebar,
  setSidebar,
  setData,
  memberRecords,
  setMemberRecords,
  labelsSet,
  setLabelsSet,
  setPedigreeState,
  personLockHook,
  bulkEditActive,
  pedigreeLockedByOther,
  carrierPresentStates,
  showCarrierMarks,
  carrierStyle,
  analysis,
  onLinkStatusChanged,
  isSaving = false
}) {
  const { theme } = useContext(ThemeContext);
  const { content } = useContext(AppContext);
  // Scope canvas node lookups to this chart (multi-pedigree-tabs); document
  // fallback keeps a lone chart unchanged.
  const { containerRef } = usePedigreeInstance();
  const {
    pedigreeSelectedUsers: users,
    settings: { epicEmrApi, pedigreeSetting, systemFeatures }
  } = content;
  // Admin-controlled feature flags. `null` while the initial fetch is in
  // flight; we treat undecided as "leave items visible" to avoid a flicker
  // where a feature briefly disappears on first paint.
  const featureFlags = useVAFeatureFlags();
  const { selectedUser } = users;
  const hasExternalIds =
    Array.isArray(selectedUser?.externalFamilyIds) &&
    selectedUser.externalFamilyIds.length > 0;
  const anonymousActive = isAnonymousActive(pedigreeSetting);
  const { user: loggedInUser, setUser } = useContext(UserContext);

  const [openModal, setOpenModal] = React.useState(false);
  const [modificationType, setModificationType] = useState(false);
  const [modificationSubType, setModificationSubType] = useState({});
  // Optional: scope the history modal to a single record (per-note history).
  const [modificationRecordId, setModificationRecordId] = useState(null);
  const accordionRefs = useRef({});

  // Dirty-state tracking for Diseases / Phenotypes / Genotypes
  const {
    markDirty,
    markClean,
    markAllClean,
    isDirty,
    dirtyCount,
    dirtySectionLabels
  } = useDirtyState();

  // Refs so PedigreeSideBar can trigger each section's save / discard
  const diagnosesSaveRef = useRef(null);
  const phenotypesSaveRef = useRef(null);
  const genotypesSaveRef = useRef(null);
  const personSaveRef = useRef(null);
  const tagsSaveRef = useRef(null);
  const externalIdsSaveRef = useRef(null);
  const familyNoteSaveRef = useRef(null);
  const diagnosesDiscardRef = useRef(null);
  const phenotypesDiscardRef = useRef(null);
  const genotypesDiscardRef = useRef(null);
  const personDiscardRef = useRef(null);
  const tagsDiscardRef = useRef(null);
  const externalIdsDiscardRef = useRef(null);
  const familyNoteDiscardRef = useRef(null);

  // Person-level lock lifecycle: acquire on person select, release on deselect
  const heartbeatIntervalRef = useRef(null);
  useEffect(() => {
    const pid = data?.pid;
    if (!pid || !personLockHook) return;

    personLockHook.acquireLock(pid);

    heartbeatIntervalRef.current = setInterval(() => {
      personLockHook.sendHeartbeat(pid);
    }, 30000);

    return () => {
      clearInterval(heartbeatIntervalRef.current);
      personLockHook.releaseLock(pid);
    };
  }, [data?.pid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release lock on tab close
  useEffect(() => {
    if (!data?.pid || !personLockHook) return;
    const pid = data.pid;
    const handleUnload = () => {
      navigator.sendBeacon(`/api/person-lock/${pid}/release`);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [data?.pid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute whether current person is locked by someone else
  const lockedByOther =
    data?.pid && personLockHook
      ? personLockHook.isLockedByOther(data.pid)
      : false;

  const lockInfo = lockedByOther
    ? personLockHook.lockedPersons.get(data.pid)
    : null;

  // Current user holds the person lock for the selected node
  const hasPersonLock =
    data?.pid && personLockHook ? personLockHook.isMine(data.pid) : false;

  let familyMemberId = useMemo(() => {
    return data?.proBandId ?? data?.familyMemberId;
  }, [data]);

  const { status: personLinkStatus, linkData: personLinkData } =
    usePersonLinkStatus(data ? familyMemberId : null);
  const isSecondaryRecord = personLinkStatus === 'secondary';
  // Sections are disabled when:
  // - locked by another person-level lock holder, OR
  // - pedigree is globally locked by another user AND this user doesn't hold
  //   the person lock for the selected node (person lock overrides pedigree lock
  //   for editing that specific person's fields), OR
  // - bulk edit is active
  const sectionDisabled =
    isSecondaryRecord ||
    lockedByOther ||
    (!hasPersonLock && !!pedigreeLockedByOther) ||
    !!bulkEditActive;

  const [recordStatusRefreshKey, setRecordStatusRefreshKey] = useState(0);
  const [familyHistoryRefreshKey, setFamilyHistoryRefreshKey] = useState(0);

  const handleSaveAll = async () => {
    if (isDirty('person')) await personSaveRef.current?.();
    if (isDirty('diagnoses')) await diagnosesSaveRef.current?.();
    if (isDirty('phenotypes')) await phenotypesSaveRef.current?.();
    if (isDirty('genotypes')) await genotypesSaveRef.current?.();
    if (isDirty('tags')) await tagsSaveRef.current?.();
    if (isDirty('externalFamilyIds')) await externalIdsSaveRef.current?.();
    if (isDirty('familyNote')) await familyNoteSaveRef.current?.();
    setRecordStatusRefreshKey((k) => k + 1);
  };

  const handleDiscardAll = () => {
    personDiscardRef.current?.();
    diagnosesDiscardRef.current?.();
    phenotypesDiscardRef.current?.();
    genotypesDiscardRef.current?.();
    tagsDiscardRef.current?.();
    externalIdsDiscardRef.current?.();
    familyNoteDiscardRef.current?.();
    markAllClean();
  };

  const handleOpenModel = () => setOpenModal(true);
  const handleCloseModel = () => setOpenModal(false);
  const canAccessExportSettings = [
    CONSTANTS.userTypes.ADMIN,
    CONSTANTS.userTypes.GENETICS,
    CONSTANTS.userTypes.GENETICS_SERVICE_ADMIN
  ].includes(loggedInUser?.userType);

  useEffect(() => {
    if (!loggedInUser || !canAccessExportSettings) return;

    axios.get(APICONSTANTS.getExportSettings).then((res) => {
      const canExport = res.data.canExport ?? false;
      setUser((prev) => {
        // prev.userGroup is the bare ObjectId from the JWT until something
        // promotes it to an object. Spreading a string loses the _id and
        // breaks any reader that needs it (e.g. the User Group chip), so
        // hoist the id into the object form first.
        const existingGroup =
          prev?.userGroup && typeof prev.userGroup === 'object'
            ? prev.userGroup
            : prev?.userGroup
            ? { _id: prev.userGroup }
            : {};
        return {
          ...prev,
          userGroup: { ...existingGroup, canExport }
        };
      });
    });
  }, []);

  let isStudent = useMemo(() => {
    return loggedInUser.userType === CONSTANTS.userTypes.STUDENT;
  }, [loggedInUser]);

  const modificationHistoryHandle = (type, subType = {}, recordId = null) => {
    setModificationType(type);
    setModificationSubType(subType);
    setModificationRecordId(recordId);
    handleOpenModel();
  };

  const unHighlightNodeByPid = (pid) => {
    // Real member pids are positive numbers (proband is 1); freshly-added,
    // unsaved members carry a UUID string. A non-positive numeric pid (e.g. the
    // -1 sentinel seen when closing the sidebar for a synthetic/transient node)
    // has no rendered <g>, so there is nothing to unhighlight — bail quietly
    // rather than log a spurious console.error on every such close.
    if (pid == null || (typeof pid === 'number' && pid < 1)) return;

    const parentGroup = d3
      .select(containerRef.current || document)
      .selectAll('g')
      .filter(function (d) {
        return d?.data?.pid === pid;
      });

    if (!parentGroup.empty()) {
      const targetPath = parentGroup.select('path.te');
      if (!targetPath.empty()) {
        targetPath
          .attr('stroke-width', 10)
          .style('stroke', theme.appearance.text);
      } else {
        console.error('No <path class="te"> found for pid:', pid);
      }
    } else {
      console.error('No <g> element found for pid:', pid);
    }
  };

  const handleClose = () => {
    setSidebar({
      open: false,
      expand: false,
      expandAccordian: []
    });

    if (data) {
      unHighlightNodeByPid(data?.canvasPid ?? data?.pid);
    }
    setData(null);
  };

  const toggleSideBar = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    if (!open) {
      handleClose();
    } else {
      const defaultExpandedAccordion = [SIDEBAR_VALUE.legend];

      // Fetch state of expanded accordion from local storage
      const savedAccordionState = localStorage.getItem('pedigreeSidebarState');
      const expandedAccordion = savedAccordionState
        ? JSON.parse(savedAccordionState)
        : null;

      // Determine the final accordion state
      const expandAccordian =
        expandedAccordion === null
          ? defaultExpandedAccordion
          : expandedAccordion;

      setSidebar((pre) => ({ ...pre, open: open, expandAccordian }));
    }
  };

  const sidebarRef = useRef();

  const handleChangeAccordian = (panel) => (event, newExpanded) => {
    if (newExpanded) localStorage.setItem('sidebar-panel', panel);
    else localStorage.setItem('sidebar-panel', null);
    setSidebar((pre) => {
      const expandAccordian = newExpanded
        ? [...pre.expandAccordian, panel]
        : [...pre.expandAccordian].filter((item) => item !== panel);

      // Save state of accordion to local storage of browser
      localStorage.setItem(
        'pedigreeSidebarState',
        JSON.stringify(expandAccordian)
      );

      return { ...pre, expandAccordian };
    });

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (newExpanded && accordionRefs.current[panel]) {
          accordionRefs.current[panel].scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } else if (sidebarRef.current) {
          sidebarRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 200);
    });
  };

  useEffect(() => {
    const panel = sidebar.scrollTo;
    if (!panel) return undefined;
    const timer = setTimeout(() => {
      accordionRefs.current[panel]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setSidebar((pre) => ({ ...pre, scrollTo: null }));
    }, 300);
    return () => clearTimeout(timer);
  }, [sidebar.scrollTo, setSidebar]);

  const handlePhenotypesToggle = (checked) => {
    setLabelsSet((prev) => ({
      ...prev,
      phenotypes: checked,
      phenotypesMap: Object.keys(prev.phenotypesMap || {}).reduce((acc, k) => {
        acc[k] = checked;
        return acc;
      }, {})
    }));
  };

  const handleGenotypesToggle = (checked) => {
    setLabelsSet((prev) => ({
      ...prev,
      genotypes: checked,
      genotypesMap: Object.keys(prev.genotypesMap || {}).reduce((acc, k) => {
        acc[k] = checked;
        return acc;
      }, {})
    }));
  };

  const [legendDiseasesOpen, setLegendDiseasesOpen] = React.useState(
    () => localStorage.getItem('legend-group-open:diseases') !== 'false'
  );
  const [legendPhenotypesOpen, setLegendPhenotypesOpen] = React.useState(
    () => localStorage.getItem('legend-group-open:phenotypes') !== 'false'
  );
  const [legendGenotypesOpen, setLegendGenotypesOpen] = React.useState(
    () => localStorage.getItem('legend-group-open:genotypes') !== 'false'
  );

  const toggleLegendGroup = (group, setOpen) => {
    setOpen((prev) => {
      localStorage.setItem(`legend-group-open:${group}`, String(!prev));
      return !prev;
    });
  };

  const legendMenu = React.useMemo(() => {
    const someDiseasesHidden =
      labelsSet?.diseases === false ||
      Object.values(labelsSet?.diseasesMap || {}).some((v) => v === false);
    const somePhenotypesHidden =
      labelsSet?.phenotypes === false ||
      Object.values(labelsSet?.phenotypesMap || {}).some((v) => v === false);
    const someGenotypesHidden =
      labelsSet?.genotypes === false ||
      Object.values(labelsSet?.genotypesMap || {}).some((v) => v === false);

    const showAllDiseases = () =>
      setLabelsSet((prev) => ({
        ...prev,
        diseases: true,
        diseasesMap: Object.keys(prev.diseasesMap || {}).reduce(
          (acc, k) => ({ ...acc, [k]: true }),
          {}
        )
      }));
    const showAllPhenotypes = () =>
      setLabelsSet((prev) => ({
        ...prev,
        phenotypes: true,
        phenotypesMap: Object.keys(prev.phenotypesMap || {}).reduce(
          (acc, k) => ({ ...acc, [k]: true }),
          {}
        )
      }));
    const showAllGenotypes = () =>
      setLabelsSet((prev) => ({
        ...prev,
        genotypes: true,
        genotypesMap: Object.keys(prev.genotypesMap || {}).reduce(
          (acc, k) => ({ ...acc, [k]: true }),
          {}
        )
      }));

    return (
      <div>
        {/* Diseases */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={labelsSet?.diseases ?? true}
              onChange={(e) =>
                setLabelsSet((prev) => ({
                  ...prev,
                  diseases: e.target.checked,
                  diseasesMap: Object.keys(prev.diseasesMap || {}).reduce(
                    (acc, k) => {
                      acc[k] = e.target.checked;
                      return acc;
                    },
                    {}
                  )
                }))
              }
              size="small"
            />
            <Typography
              variant="body1"
              onClick={() =>
                toggleLegendGroup('diseases', setLegendDiseasesOpen)
              }
              sx={{
                fontWeight: 'bold',
                flexGrow: 1,
                cursor: 'pointer',
                userSelect: 'none',
                color: theme.header
              }}
            >
              Diseases
            </Typography>
            {someDiseasesHidden && (
              <Button
                size="small"
                variant="text"
                onClick={showAllDiseases}
                sx={{ fontSize: '0.7rem', py: 0, px: 0.5, minWidth: 'auto' }}
              >
                Show all
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() =>
                toggleLegendGroup('diseases', setLegendDiseasesOpen)
              }
            >
              {legendDiseasesOpen ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </div>
          <Collapse in={legendDiseasesOpen}>
            <LegendMenu
              diagnoses={users.diagnoses}
              id="diagnoses-legend-sidebar"
              labelsSet={labelsSet}
              setLabelsSet={setLabelsSet}
              showLegendTxt
              showCheckboxes={true}
              showMasterCheckbox={false}
              dataset={memberRecords}
              carrierPresentStates={carrierPresentStates}
              showCarrierMarks={showCarrierMarks}
              carrierStyle={carrierStyle}
              showRecordLegend
            />
          </Collapse>
        </div>
        {/* Phenotypes */}
        {users.phenotypes?.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={labelsSet?.phenotypes ?? true}
                onChange={(e) => handlePhenotypesToggle(e.target.checked)}
                size="small"
                name="phenotypes"
              />
              <Typography
                variant="body1"
                onClick={() =>
                  toggleLegendGroup('phenotypes', setLegendPhenotypesOpen)
                }
                sx={{
                  fontWeight: 'bold',
                  flexGrow: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: theme.header
                }}
              >
                Phenotypes
              </Typography>
              {somePhenotypesHidden && (
                <Button
                  size="small"
                  variant="text"
                  onClick={showAllPhenotypes}
                  sx={{ fontSize: '0.7rem', py: 0, px: 0.5, minWidth: 'auto' }}
                >
                  Show all
                </Button>
              )}
              <IconButton
                size="small"
                onClick={() =>
                  toggleLegendGroup('phenotypes', setLegendPhenotypesOpen)
                }
              >
                {legendPhenotypesOpen ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </div>
            <Collapse in={legendPhenotypesOpen}>
              <PhenotypesLegend
                phenotypes={users.phenotypes}
                id="phenotypes-legend-sidebar"
                dataset={memberRecords}
                setDataset={setMemberRecords}
                labelsSet={labelsSet}
                setLabelsSet={setLabelsSet}
                showCheckboxes={true}
                draggable={true}
              />
            </Collapse>
          </div>
        )}
        {/* Genotypes */}
        {users.genotypes?.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={labelsSet?.genotypes ?? true}
                onChange={(e) => handleGenotypesToggle(e.target.checked)}
                size="small"
                name="genotypes"
              />
              <Typography
                variant="body1"
                onClick={() =>
                  toggleLegendGroup('genotypes', setLegendGenotypesOpen)
                }
                sx={{
                  fontWeight: 'bold',
                  flexGrow: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: theme.header
                }}
              >
                Genotypes
              </Typography>
              {someGenotypesHidden && (
                <Button
                  size="small"
                  variant="text"
                  onClick={showAllGenotypes}
                  sx={{ fontSize: '0.7rem', py: 0, px: 0.5, minWidth: 'auto' }}
                >
                  Show all
                </Button>
              )}
              <IconButton
                size="small"
                onClick={() =>
                  toggleLegendGroup('genotypes', setLegendGenotypesOpen)
                }
              >
                {legendGenotypesOpen ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </div>
            <Collapse in={legendGenotypesOpen}>
              <GenotypesLegend
                genotypes={users.genotypes}
                id="genotypes-legend-sidebar"
                dataset={memberRecords}
                setDataset={setMemberRecords}
                labelsSet={labelsSet}
                setLabelsSet={setLabelsSet}
                showCheckboxes={true}
                draggable={true}
              />
            </Collapse>
          </div>
        )}
      </div>
    );
  }, [
    labelsSet,
    memberRecords,
    legendDiseasesOpen,
    legendPhenotypesOpen,
    legendGenotypesOpen,
    carrierStyle,
    carrierPresentStates,
    showCarrierMarks
  ]);

  const addFamily = React.useMemo(() => {
    return (
      <AddRelativeMenu
        setPedigreeState={setPedigreeState}
        node={data}
        user={selectedUser}
        handleDetailsChange={() => {}}
        setMemberRecords={setMemberRecords}
        memberRecords={memberRecords}
        onCloseMenu={() => {}}
        sidebar={true}
        // Adds stay disabled for the WHOLE save round trip (fire →
        // response-merge): a click landing mid-flight used to get its
        // structural edits reverted by the response reconciliation.
        isDisabled={sectionDisabled || isSaving}
      />
    );
  }, [data, memberRecords, sectionDisabled, isSaving]);

  const getAccordianDetails = (value) => {
    switch (value) {
      case SIDEBAR_VALUE.legend:
        return legendMenu;
      case SIDEBAR_VALUE.person:
        return (
          <PersonMenu
            setPedigreeState={setPedigreeState}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            onDirtyChange={(d) =>
              d ? markDirty('person') : markClean('person')
            }
            saveRef={personSaveRef}
            discardRef={personDiscardRef}
            isDisabled={sectionDisabled}
            modificationHistoryHandle={modificationHistoryHandle}
            onProbandEmailSaved={() => setFamilyHistoryRefreshKey((k) => k + 1)}
            onSelectMember={(pid) =>
              setSidebar((pre) => ({ ...pre, selectPid: pid }))
            }
          />
        );
      case SIDEBAR_VALUE.tags:
        // Person view (a node is selected) → person-level tags; family view
        // (no node) → the existing family-level tags. Both reuse the tag refs.
        return data ? (
          <PersonTags
            data={data}
            user={selectedUser}
            onDirtyChange={(d) => (d ? markDirty('tags') : markClean('tags'))}
            saveRef={tagsSaveRef}
            discardRef={tagsDiscardRef}
          />
        ) : (
          <Tags
            user={selectedUser}
            onDirtyChange={(d) => (d ? markDirty('tags') : markClean('tags'))}
            saveRef={tagsSaveRef}
            discardRef={tagsDiscardRef}
          />
        );
      case SIDEBAR_VALUE.externalFamilyIds:
        return (
          <ExternalFamilyIds
            user={selectedUser}
            onDirtyChange={(d) =>
              d
                ? markDirty('externalFamilyIds')
                : markClean('externalFamilyIds')
            }
            saveRef={externalIdsSaveRef}
            discardRef={externalIdsDiscardRef}
          />
        );
      case SIDEBAR_VALUE.familyNote:
        return (
          <FamilyNote
            user={selectedUser}
            onDirtyChange={(d) =>
              d ? markDirty('familyNote') : markClean('familyNote')
            }
            saveRef={familyNoteSaveRef}
            discardRef={familyNoteDiscardRef}
          />
        );
      case SIDEBAR_VALUE.familyHistory:
        return (
          <FamilyHistoryMenu
            user={selectedUser}
            active={
              sidebar.expandAccordian?.includes(SIDEBAR_VALUE.familyHistory) ||
              false
            }
            refreshKey={familyHistoryRefreshKey}
          />
        );
      case SIDEBAR_VALUE.addRelative:
        return addFamily;
      case SIDEBAR_VALUE.labels:
        return (
          <LabelsMenu
            labelsSet={labelsSet}
            setLabelsSet={setLabelsSet}
            isFamilyView={!data}
            diagnoses={users.diagnoses}
          />
        );
      case SIDEBAR_VALUE.riskFactors:
        return (
          <RiskFactorsMenu
            data={data}
            modificationHistoryHandle={modificationHistoryHandle}
          />
        );
      case SIDEBAR_VALUE.export:
        return <ExportMenu data={data} user={selectedUser} />;
      case SIDEBAR_VALUE.phenotypes:
        return (
          <PhenotypesMenu
            handleClose={handleClose}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            setPedigreeState={setPedigreeState}
            setLabelsSet={setLabelsSet}
            onDirtyChange={(d) =>
              d ? markDirty('phenotypes') : markClean('phenotypes')
            }
            saveRef={phenotypesSaveRef}
            discardRef={phenotypesDiscardRef}
            isDisabled={sectionDisabled}
          />
        );
      case SIDEBAR_VALUE.genotypes:
        return (
          <GenotypesMenu
            handleClose={handleClose}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            setPedigreeState={setPedigreeState}
            setLabelsSet={setLabelsSet}
            onDirtyChange={(d) =>
              d ? markDirty('genotypes') : markClean('genotypes')
            }
            saveRef={genotypesSaveRef}
            discardRef={genotypesDiscardRef}
            isDisabled={sectionDisabled}
          />
        );
      case SIDEBAR_VALUE.diagnoses:
        return (
          <DiagnosesMenu
            handleClose={handleClose}
            data={data}
            setMemberRecords={setMemberRecords}
            memberRecords={memberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            setPedigreeState={setPedigreeState}
            setLabelsSet={setLabelsSet}
            onDirtyChange={(d) =>
              d ? markDirty('diagnoses') : markClean('diagnoses')
            }
            saveRef={diagnosesSaveRef}
            discardRef={diagnosesDiscardRef}
            isDisabled={sectionDisabled}
          />
        );
      case SIDEBAR_VALUE.suggestions:
        return (
          <Suggestions
            data={data}
            setDataset={setMemberRecords}
            analysis={analysis}
          />
        );
      case SIDEBAR_VALUE.pedigreeMatch:
        return (
          <PedigreeMatchMenu
            analysis={analysis}
            data={data}
            memberRecords={memberRecords}
          />
        );
      case SIDEBAR_VALUE.emr:
        return <EpicEmrButtonWrapper data={data} emrSettings={epicEmrApi} />;
      case SIDEBAR_VALUE.clinicalNotes:
        return (
          <ClinicalNotes
            data={data}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            modificationHistoryHandle={modificationHistoryHandle}
            isDisabled={sectionDisabled}
            addFetusEvent={sidebar?.addFetusEvent}
            onAddFetusEventHandled={() =>
              setSidebar((pre) => ({ ...pre, addFetusEvent: null }))
            }
          />
        );
      case SIDEBAR_VALUE.reproductive:
        return (
          <ReproductiveSection
            data={data}
            openRecordId={sidebar?.openReproRecordId}
            onSelectMember={(pid) =>
              setSidebar((pre) => ({ ...pre, selectPid: pid }))
            }
            onAddFetusEvent={(scope) =>
              setSidebar((pre) => ({
                ...pre,
                open: true,
                // Switch the clinician to Clinical Records, where the record form lives, and carry
                // the fetus scope with them.
                expandAccordian: [SIDEBAR_VALUE.clinicalNotes],
                addFetusEvent: scope
              }))
            }
            onOpenRecordHandled={() =>
              setSidebar((pre) => ({ ...pre, openReproRecordId: null }))
            }
            user={selectedUser}
            memberRecords={memberRecords}
            setMemberRecords={setMemberRecords}
            setPedigreeState={setPedigreeState}
            modificationHistoryHandle={modificationHistoryHandle}
            isDisabled={sectionDisabled || isSaving}
          />
        );
      case SIDEBAR_VALUE.formSubmission:
        return <FormSubmission data={data} />;
      case SIDEBAR_VALUE.pathways:
        return <Pathways data={data} />;
      case SIDEBAR_VALUE.recordStatus:
        return (
          <RecordStatusMenu
            data={data}
            refreshKey={recordStatusRefreshKey}
            onLinkStatusChanged={onLinkStatusChanged}
          />
        );
      default:
        return <div>New Feature Coming Soon...</div>;
    }
  };

  const getSideBarItems = () => {
    const baseItems = isStudent
      ? STUDENT_SIDEBAR_ITEMS
      : data
      ? getUserSidebarItems({
          showEmrTab: epicEmrApi?.enabled,
          // REPRODUCTIVE is hidden for an assigned-male individual (TGCLOUD-1617 item 6). Read the
          // member from memberRecords where possible — `data` can predate hydration, and the
          // pregnancies list is what keeps an already-recorded pregnancy reachable.
          member: (memberRecords || []).find((m) => m.pid === data?.pid) || data
        })
      : SIDEBAR_ITEMS;

    const canExport = !!loggedInUser?.userGroup?.canExport;

    const sidebarItems = baseItems.filter((item) => {
      if (!canExport && item.value === SIDEBAR_VALUE.export) return false;
      // Add Relative requires the pedigree lock — hide it for other users
      if (pedigreeLockedByOther && item.value === SIDEBAR_VALUE.addRelative)
        return false;
      // Clinical Insights gated by the admin's Virtual Assistant toggle.
      // `featureFlags` is null on first paint — keep the item visible until
      // we know, to avoid a brief disappearing accordion when flags load.
      // Pedigree Match shares this gate — part of the same
      // analysis/clinical-insights prototype, hidden when Clinical Insights is
      // disabled.
      if (
        (item.value === SIDEBAR_VALUE.suggestions ||
          item.value === SIDEBAR_VALUE.pedigreeMatch) &&
        featureFlags &&
        !featureFlags.clinicalInsightsEnabled
      ) {
        return false;
      }
      // Family History follows the Family History Questionnaire platform
      // feature — on by default, hidden only when explicitly disabled.
      if (
        item.value === SIDEBAR_VALUE.familyHistory &&
        systemFeatures?.FEATURE_FAMILY_HISTORY_QUESTIONNAIRE === false
      ) {
        return false;
      }
      // Pathways follows the Pathways system feature — on by default, hidden
      // only when an admin has explicitly disabled it in settings.
      if (
        item.value === SIDEBAR_VALUE.pathways &&
        systemFeatures?.FEATURE_PATHWAYS === false
      ) {
        return false;
      }
      // Anonymous pedigrees: external family IDs are removed by decision,
      // EMR/SMART is incompatible, and form submission / patient access
      // need a patient email that cannot exist. Backend rejects all four.
      if (
        anonymousActive &&
        [
          SIDEBAR_VALUE.externalFamilyIds,
          SIDEBAR_VALUE.emr,
          SIDEBAR_VALUE.formSubmission,
          SIDEBAR_VALUE.setupPatientAccess
        ].includes(item.value)
      ) {
        return false;
      }
      return true;
    });

    return sidebarItems;
  };

  const getExpandedValue = (val) => {
    return sidebar.expandAccordian?.includes(val) ? true : false;
  };

  let headerTitle = useMemo(() => {
    let proBandId = data?.proBandId;
    let familyMemberId = data?.familyMemberId;
    if (proBandId || familyMemberId) return proBandId || familyMemberId;
    return selectedUser?.familyId;
  }, [selectedUser, data]);

  const list = (anchor) => (
    <StyledList
      role="presentation"
      expand={sidebar.expand}
      full={anchor === 'top' || anchor === 'bottom'}
    >
      {/* Header Section */}
      <HeaderSection>
        {/* Header Title (full-screen expand toggle removed — never used, and the
            sidebar layout isn't optimised for the full-width view) */}
        <Typography
          component="h2"
          style={{ color: theme.header, fontWeight: 700, fontSize: '1.5rem' }}
        >
          {headerTitle}
        </Typography>

        {/* Close Button */}
        <CloseBtn onClick={toggleSideBar(false)}>
          <HighlightOffIcon />
        </CloseBtn>
      </HeaderSection>

      {/* Lock banner — shown when this person is edited by another user */}
      {lockedByOther && (
        <SidebarLockBanner
          lockInfo={lockInfo}
          requestStatus={personLockHook.requestStatuses?.get(data?.pid)}
          onRequest={() => personLockHook.requestLock(data.pid)}
          onCancelRequest={() => personLockHook.cancelRequestLock(data.pid)}
          onForceAcquire={() => personLockHook.forceAcquireLock(data.pid)}
        />
      )}

      {/* Bulk edit active banner */}
      {bulkEditActive && !lockedByOther && (
        <Box
          sx={{
            mx: 1,
            my: 1,
            px: 2,
            py: 1.5,
            bgcolor: 'grey.100',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            Sidebar editing is unavailable while a bulk edit session is in
            progress.
          </Typography>
        </Box>
      )}

      {isSecondaryRecord && !lockedByOther && !bulkEditActive && (
        <Alert severity="warning" sx={{ flexShrink: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600
            }}
          >
            Secondary Record Editing Locked
          </Typography>
          <Typography variant="caption">
            Primary:{' '}
            <strong>{personLinkData?.primaryFamilyMemberId || '—'}</strong>
          </Typography>
        </Alert>
      )}

      <ScrollableContent ref={sidebarRef}>
        {/* Configurations — family pedigree sidebar only (no node selected) */}
        {!data && (
          <LabelConfigurations
            labelsSet={labelsSet}
            setLabelsSet={setLabelsSet}
            hasExternalIds={hasExternalIds}
          />
        )}

        {/* Sidebar Items */}
        <ListContainer>
          {getSideBarItems().map((item) => (
            <SidebarSection
              key={item.value}
              item={item}
              expanded={getExpandedValue(item.value)}
              onChange={handleChangeAccordian(item.value)}
              summaryRef={(el) => (accordionRefs.current[item.value] = el)}
              dirty={
                [
                  SIDEBAR_VALUE.person,
                  SIDEBAR_VALUE.diagnoses,
                  SIDEBAR_VALUE.phenotypes,
                  SIDEBAR_VALUE.genotypes
                ].includes(item.value) && isDirty(item.value)
              }
            >
              {getAccordianDetails(item.value)}
            </SidebarSection>
          ))}
        </ListContainer>
      </ScrollableContent>

      {!sectionDisabled && (
        <SaveFooter
          dirtyCount={dirtyCount}
          dirtySectionLabels={dirtySectionLabels}
          onSave={handleSaveAll}
          onDiscard={handleDiscardAll}
        />
      )}
    </StyledList>
  );

  return (
    <div>
      <BtnContainer style={{ display: 'none' }}>
        <IconButton onClick={toggleSideBar(true)} size="large">
          <MenuIcon />
        </IconButton>
      </BtnContainer>
      {openModal && (
        <CustomDialog
          title={
            modificationRecordId
              ? 'Record History'
              : modalTitleMap[modificationType]
              ? modalTitleMap[modificationType]
              : modificationType
          }
          open={openModal}
          handleClose={handleCloseModel}
          minHeight="auto"
          maxWidth="lg"
          fullWidth
        >
          <ModificationHistoryModal
            open={openModal}
            modificationType={modificationType}
            modificationSubType={modificationSubType}
            recordId={modificationRecordId}
            handleClose={handleCloseModel}
            familyMemberId={familyMemberId}
          />
        </CustomDialog>
      )}
      {/* SideBar */}
      <Drawer
        variant="persistent"
        anchor="right"
        open={sidebar.open}
        onClose={toggleSideBar(false)}
        sx={{
          zIndex: 2100,
          '& .MuiDrawer-paper': {
            top: '65px',
            bottom: 0,
            height: 'auto'
          }
        }}
        slotProps={{
          backdrop: { invisible: true }
        }}
      >
        {list('right')}
      </Drawer>
    </div>
  );
}
